import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import Select from 'react-select';
import classNames from 'classnames';
import styles from '../clientForm/clientForm.module.scss';
import stylesForms from '../formColumns/forms.module.scss';

export const SelectInput = (props) => {
  const {
    name,
    label,
    options,
    mode,
    required,
    searchable,
    icon,
    fieldDescription,
    focused,
    isFocused,
    placeholder,
    disabled,
    formatOptionLabel,
    clearable,
    originalValue: propOriginalValue, // Allow override of value from props

    valueColor,
  } = props;

  const customStyles = {
    menu: (provided, state) => ({
      ...provided,
      textAlign: 'left',
      padding: 0,
    }),
    singleValue: (provided, state) => ({
      ...provided,
      paddingLeft: icon ? '25px' : 0,
      color: valueColor,
    }),

    placeholder: (provided, state) => ({
      ...provided,
      textAlign: 'left',
      marginLeft: 0,
      padding: 0,
      paddingLeft: icon ? '25px' : 0,
    }),
  };

  // Try to access form context but provide fallback values
  const formContext = useFormContext();
  const hasFormContext = !!formContext;
  
  // Always call useWatch (hooks must be called consistently)
  const watchedValue = useWatch({ name });
  
  // If we have form context, use it; otherwise provide empty handlers
  const {
    register = () => {},
    formState = { errors: {}, touchedFields: {} },
    unregister = () => {},
    setValue = () => {},
  } = formContext || {};
  
  // Get errors from form context
  const errors = formState.errors;
  
  // Special handling for client_id to ensure we always have a value
  useEffect(() => {
    if (name === 'client_id' && propOriginalValue && watchedValue === undefined && hasFormContext) {
      setValue(name, propOriginalValue, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [name, propOriginalValue, watchedValue, hasFormContext, setValue]);
  
  // Priority: 1. propOriginalValue, 2. watchedValue (only if has form context), 3. null
  const originalValue = propOriginalValue !== undefined ? propOriginalValue : 
                        (hasFormContext && watchedValue !== undefined ? watchedValue : null);
  


  // Only register with the form if we have form context
  useEffect(() => {
    if (hasFormContext) {
      register(name);
    }
  }, [hasFormContext, register, name]);



  const value = () => {
    if (mode === 'multiple') {
      return valueMultiple();
    } else {
      return valueOne();
    }
  };

  const valueMultiple = () => {
    if (originalValue && options) {
      return originalValue?.map((value) => options?.find((option) => option.value == value));
    }

    return [];
  };

  const valueOne = () => {
    // If we have a value and options
    if (originalValue != null && options && options.length > 0) {
      // Find the option that matches our value
      const foundOption = options.find((option) => {
        return String(option.value) === String(originalValue);
      });
      
      if (foundOption) {
        return foundOption;
      }
    }
    
    return null;
  };

  const handleSelect = (data) => {
    const { mode, onChange } = props;

    // Only call onChange if both data exists and onChange is a function
    if (onChange && typeof onChange === 'function' && data) {
      onChange(name, data);
    }
    
    if (mode === 'multiple') {
      return handleSelectMultiple(data);
    } else {
      return handleSelectOne(data);
    }
  };

  const handleSelectMultiple = (values) => {
    if (!values) {
      setValue(name, [], { shouldValidate: true });

      return;
    }

    const newValue = values
      ?.map((data) => (data ? data.value : data))
      .filter((value) => value != null);

    setValue(name, newValue, { shouldValidate: true });
  };

  const handleSelectOne = (data) => {
    if (!data) {
      // If we have form context, set the value in the form
      if (hasFormContext) {
        setValue(name, null, { shouldValidate: true });
      }
      return;
    }

    // If we have form context, set the value in the form
    if (hasFormContext) {
      setValue(name, data.value, { shouldValidate: true });
    }
  };

  return (
    <div className={`form-group ${label ? `mt-sm-2 pt-2 ` : `mt-2`}`} style={{ clear: 'both' }}>
      <span
        className={classNames(
          'form-elements',
          stylesForms.inputWrapper,
          { [stylesForms.none]: isFocused },
          { [stylesForms.inputFocused]: focused },
          { 'border-danger': errors[name]?.message },
        )}
      >
        <span
          className={classNames('form-elements-wrapper', stylesForms.inputInnerWrapper, {
            [stylesForms.hasIcon]: icon,
          })}
        >
          {label && (
            <label className={stylesForms.label} htmlFor={name}>
              {required && <small className="text-danger">* </small>}
              {label}
            </label>
          )}
          {icon && (
            <label
              style={{ zIndex: 9, marginLeft: '2px' }}
              className={classNames(stylesForms.icon)}
            >
              {icon}
            </label>
          )}

          {fieldDescription && <span className={styles.clientDescription}>{fieldDescription}</span>}
          <span className="form-element">
            <Select
              onChange={handleSelect}
              options={options || []}
              value={value()}
              isMulti={mode === 'multiple'}
              styles={customStyles}
              isSearchable={searchable}
              placeholder={placeholder}
              isDisabled={disabled}
              isClearable={clearable}
              className={`${errors[name]?.message ? 'selectInputStylesContainer border-danger' : 'selectInputStylesContainer'}`}
              classNamePrefix={'selectInputStyles'}
              formatOptionLabel={formatOptionLabel}
            />
          </span>
        </span>
        <small className="text-danger float-start" style={{ clear: 'both' }}>
          {errors[name]?.message}
        </small>
      </span>
    </div>
  );
};
